import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getToken, logout } from "../services/authService";
import { useCurrency } from '../context/CurrencyContext';
import svgUser from '../assets/Navbar/user.svg';
import logo from '../assets/Navbar/globe-logo.svg';

export default function AdminHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const profileRef = useRef(null);
  const currencyRef = useRef(null);
  const { currency, changeCurrency } = useCurrency();
  const hasToken = !!getToken();

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setShowCurrencyMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getPageTitle = () => {
    const path = location.pathname.replace(/\/$/, "");
    if (path === '/admin') return "Welcome to the Dashboard";
    if (path.startsWith('/admin/orders')) return "Orders";
    if (path.startsWith('/admin/reports')) return "Reports & Dashboard";
    if (path.startsWith('/admin/products')) return "Products";
    if (path.startsWith('/admin/users')) return "User Management";
    if (path.startsWith('/admin/pages')) return "Page Management";
    if (path.startsWith('/admin/media')) return "Media & Files";
    if (path.startsWith('/admin/chat')) return "Chat Support";
    if (path.startsWith('/admin/blogs')) return "Manage Blogs";
    if (path.startsWith('/admin/categories')) return "Product Categories";
    if (path.startsWith('/admin/settings')) return "System Settings";
    if (path.startsWith('/admin/shops')) return "Lokacionet e Dyqaneve";
    if (path.startsWith('/admin/services')) return "Services";
    return "Admin Console";
  };

  return (
    <header className="admin-top-header" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0 24px',
      height: '60px',
      background: '#fff',
      borderBottom: '1px solid #e2e8f0',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
    }}>
      <div className="admin-header-left" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
           <img src={logo} alt="GLOBE" style={{ height: '32px' }} />
        </Link>
        <span style={{ fontSize: '20px', color: '#0f172a', fontWeight: 700, borderLeft: '1px solid #e2e8f0', paddingLeft: '20px' }}>{getPageTitle()}</span>
      </div>

      <div className="admin-header-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Link to="/" style={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#3b82f6',
          textDecoration: 'none',
          padding: '6px 12px',
          borderRadius: '6px',
          background: '#eff6ff',
          transition: 'all 0.2s'
        }} onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe' }} onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff' }}>
          Back to Store
        </Link>

        {/* Currency Switcher */}
        <div ref={currencyRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowCurrencyMenu(s => !s)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              color: '#334155',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc' }}
          >
            <span>Currency: <strong>{currency}</strong></span>
            <span style={{ fontSize: '10px', color: '#64748b' }}>▼</span>
          </button>
          {showCurrencyMenu && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '8px',
              background: '#fff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
              padding: '4px',
              borderRadius: '8px',
              zIndex: 100,
              minWidth: '100px'
            }}>
              {['ALL', 'EUR', 'USD'].map(code => (
                <button
                  key={code}
                  type="button"
                  onClick={() => { changeCurrency(code); setShowCurrencyMenu(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    background: currency === code ? '#eff6ff' : 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: currency === code ? '#3b82f6' : '#334155',
                    fontSize: '13px',
                    fontWeight: 500,
                    borderRadius: '6px'
                  }}
                  onMouseEnter={(e) => { if (currency !== code) e.currentTarget.style.background = '#f8fafc' }}
                  onMouseLeave={(e) => { if (currency !== code) e.currentTarget.style.background = 'none' }}
                >
                  {code}
                </button>
              ))}
            </div>
          )}
        </div>

        <div ref={profileRef} style={{ position: 'relative' }}>
          <button type="button" onClick={() => setShowProfileMenu(s => !s)} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            transition: 'background 0.2s',
            padding: 0
          }} onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}>
            <img src={svgUser} alt="Profile" style={{ width: '20px', height: '20px' }} />
          </button>
          
          {showProfileMenu && hasToken && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '8px',
              background: '#fff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
              padding: '8px',
              borderRadius: '8px',
              zIndex: 100,
              minWidth: '160px'
            }}>
              <Link to="/profile" onClick={() => setShowProfileMenu(false)} style={{
                display: 'block',
                padding: '8px 12px',
                textDecoration: 'none',
                color: '#334155',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px'
              }} onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}>
                Profili
              </Link>
              <button type="button" onClick={() => { setShowProfileMenu(false); handleLogout(); }} style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ef4444',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px'
              }} onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}>
                Çkyçu
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
